import { propertyHooks, userHooks } from '$src/lib/trpcHooks'
import { Box, Checkbox, List, Loader, Stack, Text, Title } from '@mantine/core'
import { AppModal, StreetViewCard } from '@markku/components'
import {
  IconCalendar,
  IconCheck,
  IconFileText,
  IconHome,
} from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

interface WelcomePropertyModalProps {
  opened: boolean
  onClose: () => void
  propertyId: number
  propertyAddress: string
}

export function WelcomePropertyModal({
  opened,
  onClose,
  propertyId,
  propertyAddress,
}: WelcomePropertyModalProps) {
  const navigate = useNavigate()
  const { data: property, isLoading } = propertyHooks.useGetById(propertyId, {
    enabled: opened,
  })
  const { mutate: updateMarketingConsent } =
    userHooks.useUpdateMarketingConsent()

  // Local state for marketing consent - defaults to true (opt-out pattern)
  const [marketingConsent, setMarketingConsent] = useState(true)

  const hasStreetViewData =
    property?.address?.streetViewCameraLat &&
    property?.address?.streetViewCameraLng &&
    property?.address?.streetViewHeading

  const handleModalClose = () => {
    // Save marketing consent preference when modal closes
    updateMarketingConsent({ marketingConsent })
    onClose()
  }

  const handleViewPropertyDetails = () => {
    // Wait for the modal to close before navigating to maintenance
    setTimeout(() => {
      navigate({
        to: '/property/$propertyId',
        params: { propertyId: String(propertyId) },
      })
    }, 350)
    handleModalClose()
  }

  const handleGoToMaintenance = () => {
    // Wait for the modal to close before navigating to maintenance
    setTimeout(() => {
      navigate({ to: '/maintenance' })
    }, 350)
    handleModalClose()
  }

  return (
    <AppModal
      opened={opened}
      onClose={handleModalClose}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="lg"
      confirmProps={{
        label: 'Kiinteistötiedot',
        onClick: handleViewPropertyDetails,
      }}
      cancelProps={{
        label: 'Huoltokirjaan',
        onClick: handleGoToMaintenance,
      }}
    >
      {isLoading ? (
        <Stack align="center" py="xl">
          <Loader size="lg" />
        </Stack>
      ) : (
        <Stack gap="xl">
          {/* Success Header */}
          <Stack gap="md" align="center" style={{ textAlign: 'center' }}>
            <Box
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-green-1)',
              }}
            >
              <IconCheck size={36} color="var(--mantine-color-green-7)" />
            </Box>
            <Title order={3}>Kiinteistösi on lisätty!</Title>
            <Text c="dimmed">{propertyAddress}</Text>
          </Stack>

          {/* Marketing Consent - Above the fold for better visibility */}
          <Checkbox
            checked={marketingConsent}
            size="md"
            onChange={(event) =>
              setMarketingConsent(event.currentTarget.checked)
            }
            label="Haluan saada Markun huoltotarjouksia ja etuja"
            description="Saat vinkkejä kodin huoltoon ja erikoistarjouksia kumppaneiltamme"
          />

          {/* Street View - The WOW Moment (if available) */}
          {hasStreetViewData && property?.address && (
            <StreetViewCard
              address={property.address}
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
            />
          )}

          {/* Fallback image/placeholder when no street view */}
          {!hasStreetViewData && (
            <Box
              style={{
                width: '100%',
                aspectRatio: '5/2',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FFF9F0 0%, #FFE8CC 100%)',
              }}
            >
              <IconHome size={80} color="var(--mantine-color-brandOrange-4)" />
            </Box>
          )}

          {/* Feature Highlights */}
          <Stack gap="md">
            <Text size="md" fw={600}>
              Huoltokirjasi on nyt valmis käyttöön
            </Text>

            <List spacing="md" size="sm">
              <List.Item
                icon={
                  <IconCalendar
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>Vuosikello</Text>
                <Text size="sm" c="dimmed">
                  Seuraa kausikohtaisia huoltotöitä ja pidä koti kunnossa ympäri
                  vuoden
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <IconHome
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>Kiinteistötiedot</Text>
                <Text size="sm" c="dimmed">
                  Tallenna kodin tiedot, kuvat ja teknisten järjestelmien
                  dokumentaatio
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <IconFileText
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>Dokumentit ja takuut</Text>
                <Text size="sm" c="dimmed">
                  Säilytä takuut, kuitit ja tärkeät asiakirjat turvassa yhdessä
                  paikassa
                </Text>
              </List.Item>
            </List>
          </Stack>
        </Stack>
      )}
    </AppModal>
  )
}
